import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Save, Loader2, Trash2, AlertTriangle, Calendar, ChevronDown, ChevronRight } from 'lucide-react';
import type { UserRole } from 'scheduler-types';
import { usePerson, type PersonSkill } from '../hooks/usePeople';
import { useUpdatePerson, useDeletePerson, useCreatePerson } from '../hooks/usePersonMutations';
import { useSkills } from '../hooks/useSkills';
import { useAdminSettings } from '../hooks/useAdminSettings';
import { AvailabilityCalendar } from './AvailabilityCalendar';

const roleOptions: { value: UserRole; label: string }[] = [
  { value: 'planner', label: 'Planner' },
  { value: 'worker', label: 'Worker' },
  { value: 'contractor', label: 'Contractor' },
  { value: 'account_manager', label: 'Account Manager' },
  { value: 'leadership', label: 'Leadership' },
  { value: 'procurement', label: 'Procurement' },
  { value: 'viewer', label: 'Viewer' },
];

interface PersonEditModalProps {
  personId: string | null; // null for creating new person
  isOpen: boolean;
  onClose: () => void;
}

export function PersonEditModal({ personId, isOpen, onClose }: PersonEditModalProps) {
  const isNewPerson = personId === null;

  const { data: person, isLoading: isLoadingPerson } = usePerson(personId ?? undefined);
  const { data: allSkills = [], isLoading: isLoadingSkills } = useSkills();

  const createMutation = useCreatePerson();
  const updateMutation = useUpdatePerson(personId ?? '');
  const deleteMutation = useDeletePerson();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<UserRole>('worker');
  const [isActive, setIsActive] = useState(true);
  const [selectedSkillIds, setSelectedSkillIds] = useState<Set<string>>(new Set());
  const [lunchStartTime, setLunchStartTime] = useState<string>('');
  const [lunchDurationMinutes, setLunchDurationMinutes] = useState<number>(30);
  const [useCompanyDefault, setUseCompanyDefault] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAvailability, setShowAvailability] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);

  const { data: adminSettings } = useAdminSettings();

  // Populate form when person data loads
  useEffect(() => {
    if (person) {
      setName(person.name);
      setEmail(person.email);
      setRole(person.role);
      setIsActive(person.isActive);
      setSelectedSkillIds(new Set(person.skills.map((s) => s.id)));
      if (person.lunchStartTime !== null && person.lunchStartTime !== undefined &&
          person.lunchDurationMinutes !== null && person.lunchDurationMinutes !== undefined) {
        setUseCompanyDefault(false);
        setLunchStartTime(person.lunchStartTime);
        setLunchDurationMinutes(person.lunchDurationMinutes);
      } else {
        setUseCompanyDefault(true);
        setLunchStartTime(adminSettings?.lunchStartTime ?? '12:00');
        setLunchDurationMinutes(adminSettings?.lunchDurationMinutes ?? 30);
      }
    } else if (isNewPerson) {
      setName('');
      setEmail('');
      setRole('worker');
      setIsActive(true);
      setSelectedSkillIds(new Set());
    }
  }, [person, isNewPerson, adminSettings]);

  const handleSkillToggle = (skillId: string) => {
    setSelectedSkillIds((prev) => {
      const next = new Set(prev);
      if (next.has(skillId)) {
        next.delete(skillId);
      } else {
        next.add(skillId);
      }
      return next;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const skillIds = Array.from(selectedSkillIds);

    if (isNewPerson) {
      createMutation.mutate(
        { name, email, role, skillIds },
        {
          onSuccess: () => onClose(),
          onError: (err) => setError(err instanceof Error ? err.message : 'Failed to create'),
        }
      );
    } else {
      updateMutation.mutate(
        {
          name,
          email,
          role,
          isActive,
          skillIds,
          lunchStartTime: useCompanyDefault ? null : lunchStartTime,
          lunchDurationMinutes: useCompanyDefault ? null : lunchDurationMinutes,
        },
        {
          onSuccess: () => onClose(),
          onError: (err) => setError(err instanceof Error ? err.message : 'Failed to update'),
        }
      );
    }
  };

  const handleDelete = () => {
    if (!personId) return;
    deleteMutation.mutate(personId, {
      onSuccess: () => onClose(),
      onError: (err) => setError(err instanceof Error ? err.message : 'Failed to delete'),
    });
  };

  if (!isOpen) return null;

  const isLoading = isLoadingPerson || isLoadingSkills;
  const isSaving = createMutation.isPending || updateMutation.isPending;
  const isDeleting = deleteMutation.isPending;

  // Handle click outside using ref-based detection
  const handleBackdropClick = (e: React.MouseEvent) => {
    // Only close if clicking outside the modal content
    if (modalContentRef.current && !modalContentRef.current.contains(e.target as Node)) {
      onClose();
    }
  };

  // Use Portal to render modal at document body level, isolating it from parent DOM
  const modalContent = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={handleBackdropClick}
    >
      {/* Modal container - scrollable if content is tall */}
      <div
        ref={modalContentRef}
        className={`relative bg-background rounded-lg shadow-xl w-full border border-border transition-all mx-4 max-h-[90vh] overflow-y-auto ${showAvailability ? 'max-w-4xl' : 'max-w-lg'}`}
      >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <h2 className="text-lg font-semibold text-foreground">
              {isNewPerson ? 'Add Person' : 'Edit Person'}
            </h2>
            <button
              onClick={onClose}
              className="p-1 rounded hover:bg-background-muted transition-colors"
            >
              <X className="h-5 w-5 text-foreground-muted" />
            </button>
          </div>

          {/* Content */}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="p-4 space-y-4">
                {error && (
                  <div className="p-3 rounded-md bg-danger-subtle border border-danger text-sm text-danger-foreground">
                    {error}
                  </div>
                )}

                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-muted mb-1">
                    Name
                  </label>
                  <input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-muted mb-1">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                {/* Role */}
                <div>
                  <label htmlFor="role" className="block text-sm font-medium text-foreground-muted mb-1">
                    Role
                  </label>
                  <select
                    id="role"
                    value={role}
                    onChange={(e) => setRole(e.target.value as UserRole)}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    {roleOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Active toggle (only for existing persons) */}
                {!isNewPerson && (
                  <div className="flex items-center gap-2">
                    <input
                      id="isActive"
                      type="checkbox"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                    />
                    <label htmlFor="isActive" className="text-sm text-foreground">
                      Active
                    </label>
                  </div>
                )}

                {/* Skills */}
                <div>
                  <label className="block text-sm font-medium text-foreground-muted mb-2">
                    Skills
                  </label>
                  {allSkills.length === 0 ? (
                    <p className="text-sm text-foreground-muted">No skills defined yet</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 border border-border rounded-md bg-background-subtle">
                      {allSkills.map((skill) => (
                        <label
                          key={skill.id}
                          className="flex items-center gap-2 cursor-pointer hover:bg-background-muted p-1 rounded"
                        >
                          <input
                            type="checkbox"
                            checked={selectedSkillIds.has(skill.id)}
                            onChange={() => handleSkillToggle(skill.id)}
                            className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                          />
                          <span className="text-sm text-foreground" title={skill.description || undefined}>
                            {skill.name}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* Lunch Break (only for existing persons) */}
                {!isNewPerson && (
                  <div className="border-t border-border pt-4">
                    <label className="block text-sm font-medium text-foreground-muted mb-2">
                      Lunch Break
                    </label>

                    <div className="flex items-center gap-2 mb-3">
                      <input
                        id="useCompanyDefault"
                        type="checkbox"
                        checked={useCompanyDefault}
                        onChange={(e) => {
                          setUseCompanyDefault(e.target.checked);
                          if (e.target.checked) {
                            setLunchStartTime(adminSettings?.lunchStartTime ?? '12:00');
                            setLunchDurationMinutes(adminSettings?.lunchDurationMinutes ?? 30);
                          }
                        }}
                        className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                      />
                      <label htmlFor="useCompanyDefault" className="text-sm text-foreground">
                        Use company default
                      </label>
                      {adminSettings && (
                        <span className="text-xs text-foreground-muted ml-2">
                          ({adminSettings.lunchStartTime}, {adminSettings.lunchDurationMinutes} min)
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-foreground-muted mb-1">Start Time</label>
                        <input
                          type="time"
                          value={lunchStartTime}
                          onChange={(e) => setLunchStartTime(e.target.value)}
                          disabled={useCompanyDefault}
                          className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:bg-background-muted"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-foreground-muted mb-1">Duration</label>
                        <select
                          value={lunchDurationMinutes}
                          onChange={(e) => setLunchDurationMinutes(Number(e.target.value))}
                          disabled={useCompanyDefault}
                          className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:bg-background-muted"
                        >
                          <option value={0}>No break</option>
                          <option value={15}>15 min</option>
                          <option value={30}>30 min</option>
                          <option value={45}>45 min</option>
                          <option value={60}>60 min</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {/* Availability Calendar (only for existing persons) */}
                {!isNewPerson && personId && person && (
                  <div className="border-t border-border pt-4">
                    <button
                      type="button"
                      onClick={() => setShowAvailability(!showAvailability)}
                      className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
                    >
                      {showAvailability ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                      <Calendar className="h-4 w-4" />
                      Manage Availability & PTO
                    </button>
                    {showAvailability && (
                      <div className="mt-4">
                        <AvailabilityCalendar personId={personId} personName={person.name} />
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between p-4 border-t border-border">
                <div>
                  {!isNewPerson && !showDeleteConfirm && (
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirm(true)}
                      className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-danger hover:bg-danger-subtle rounded-md transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete
                    </button>
                  )}
                  {showDeleteConfirm && (
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-danger" />
                      <span className="text-sm text-danger">Confirm delete?</span>
                      <button
                        type="button"
                        onClick={handleDelete}
                        disabled={isDeleting}
                        className="px-2 py-1 text-sm font-medium text-white bg-danger rounded hover:bg-danger/90 disabled:opacity-50"
                      >
                        {isDeleting ? 'Deleting...' : 'Yes'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowDeleteConfirm(false)}
                        className="px-2 py-1 text-sm font-medium text-foreground-muted hover:text-foreground"
                      >
                        No
                      </button>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={isSaving}
                    className="px-4 py-2 text-sm font-medium text-foreground-muted hover:text-foreground border border-border rounded-md hover:bg-background-muted transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-foreground bg-primary hover:bg-primary-hover rounded-md transition-colors disabled:opacity-50"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4" />
                        {isNewPerson ? 'Create' : 'Save'}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
  );

  return createPortal(modalContent, document.body);
}
